/**
 * Default material library for new documents.
 *
 * Seeds the project with the classic CAD-style defaults (Generic, Concrete
 * Cast-in-Place, Gypsum Board, Metal Stud, Air Space, Wood, Glass, ...) plus the
 * legacy takeoff catalog ids so existing element references and CATEGORY_MATERIALS
 * mappings keep resolving by id after the registry became real data.
 */

import { v4 as uuidv4 } from 'uuid';
import {
    AppearanceAsset,
    BIMMaterial,
    MaterialClass,
    defaultGraphics,
    defaultIdentity,
    defaultPhysical,
    defaultThermal,
} from './MaterialTypes';
import { STANDARD_MATERIALS } from '../tools/MaterialTakeoffTool';

export interface MaterialLibrarySeed {
    materials: BIMMaterial[];
    assets: AppearanceAsset[];
}

interface SeedSpec {
    /** Stable id — keep legacy ids for catalog compatibility. */
    id: string;
    name: string;
    cls: MaterialClass;
    color: string;
    roughness: number;
    metalness: number;
    transparency?: number;
    cutPatternId?: string;
    surfacePatternId?: string;
    description?: string;
    manufacturer?: string;
    typeMark?: string;
    keynote?: string;
    cost?: string;
    costBasis?: string;
    density?: number;
    youngsModulus?: number;
    shearModulus?: number;
    poissonRatio?: number;
    yieldStrength?: number;
    compressionStrength?: number;
    thermalExpansion?: number;
    conductivity?: number;
    specificHeat?: number;
    emissivity?: number;
    tileSize?: number;
    bumpStrength?: number;
}

// Foreground hatch tone derived from base color for readability.
const seed = (s: SeedSpec): { material: BIMMaterial; asset: AppearanceAsset } => {
    const asset: AppearanceAsset = {
        id: `asset-${s.id}`,
        name: s.name,
        color: s.color,
        roughness: s.roughness,
        metalness: s.metalness,
        transparency: s.transparency ?? 0,
        imageId: '',
        tileSize: s.tileSize ?? 1,
        bumpStrength: s.bumpStrength ?? 0,
        emissionColor: '',
        emissionStrength: 0,
    };
    const material: BIMMaterial = {
        id: s.id,
        name: s.name,
        appearanceAssetId: asset.id,
        identity: defaultIdentity(s.cls),
        graphics: defaultGraphics({
            cutPatternId: s.cutPatternId ?? 'fp-none',
            surfacePatternId: s.surfacePatternId ?? 'fp-none',
        }),
        physical: defaultPhysical({
            density: s.density ?? 1000,
            youngsModulus: s.youngsModulus ?? 0,
            shearModulus: s.shearModulus ?? 0,
            poissonRatio: s.poissonRatio ?? 0.25,
            yieldStrength: s.yieldStrength ?? 0,
            compressionStrength: s.compressionStrength ?? 0,
            thermalExpansion: s.thermalExpansion ?? 0.000012,
        }),
        thermal: defaultThermal({
            conductivity: s.conductivity ?? 0.5,
            specificHeat: s.specificHeat ?? 1000,
            emissivity: s.emissivity ?? 0.9,
        }),
    };
    material.identity.description = s.description ?? '';
    material.identity.manufacturer = s.manufacturer ?? '';
    material.identity.typeMark = s.typeMark ?? '';
    material.identity.keynote = s.keynote ?? '';
    material.identity.cost = s.cost ?? '';
    material.identity.costBasis = s.costBasis ?? 'm²';
    material.identity.takeoffUnit =
        (['each', 'm', 'm²', 'm³', 'kg', 'ton'] as const).includes(s.costBasis as any)
            ? (s.costBasis as BIMMaterial['identity']['takeoffUnit'])
            : 'm²';
    return { material, asset };
};

const SEED_SPECS: SeedSpec[] = [
    {
        id: 'Generic', name: 'Generic', cls: 'Miscellaneous', color: '#CCCCCC',
        roughness: 0.9, metalness: 0, cutPatternId: 'fp-ansi31', description: 'Default project material.',
        density: 1602, youngsModulus: 7030680, conductivity: 0.144, specificHeat: 1670,
    },
    {
        id: 'Air Space', name: 'Air Space', cls: 'Miscellaneous', color: '#EAF4FF',
        roughness: 1, metalness: 0, transparency: 0.6, description: 'Non-enclosed air cavity.',
        density: 1.225, conductivity: 0.025, specificHeat: 1006,
    },
    {
        id: 'Concrete - Cast-in-Place', name: 'Concrete - Cast-in-Place', cls: 'Concrete', color: '#B8B8B4',
        roughness: 0.95, metalness: 0, cutPatternId: 'fp-concrete', tileSize: 0.5, bumpStrength: 0.25,
        density: 2403, youngsModulus: 24821130, shearModulus: 10342137, poissonRatio: 0.2,
        compressionStrength: 27.579, thermalExpansion: 0.0000099, conductivity: 1.442, specificHeat: 880,
        costBasis: 'm³',
    },
    {
        id: 'Gypsum', name: 'Gypsum - Wallboard', cls: 'Finishes', color: '#F2EFE6',
        roughness: 0.9, metalness: 0, cutPatternId: 'fp-gypsum', tileSize: 1.2,
        description: '12.5 mm gypsum board finish.',
        density: 800, youngsModulus: 3930000, conductivity: 0.21, specificHeat: 1090,
    },
    {
        id: 'Metal Stud', name: 'Metal Stud', cls: 'Metal', color: '#C4C4CC',
        roughness: 0.4, metalness: 0.85, cutPatternId: 'fp-parallel-90',
        density: 7849, youngsModulus: 205000, shearModulus: 79300, poissonRatio: 0.3,
        yieldStrength: 344, thermalExpansion: 0.0000117, conductivity: 44.947, specificHeat: 482.6,
    },
    {
        id: 'Masonry - Concrete Block', name: 'Masonry - Concrete Block', cls: 'Masonry', color: '#A9A39A',
        roughness: 0.95, metalness: 0, cutPatternId: 'fp-block', surfacePatternId: 'fp-block',
        tileSize: 0.4, bumpStrength: 0.35, description: '200 mm concrete masonry unit.',
        density: 1681, youngsModulus: 14708420, compressionStrength: 13.79, conductivity: 0.584, specificHeat: 880,
    },
    {
        id: 'Masonry - Brick', name: 'Masonry - Brick', cls: 'Masonry', color: '#9E4A3A',
        roughness: 0.9, metalness: 0, cutPatternId: 'fp-brick', surfacePatternId: 'fp-brick',
        tileSize: 0.24, bumpStrength: 0.45, manufacturer: 'Standard', keynote: 'K-001',
        density: 1922, youngsModulus: 15168550, compressionStrength: 34.47, conductivity: 0.692, specificHeat: 799,
    },
    {
        id: 'Wood', name: 'Wood - Framing', cls: 'Wood', color: '#C8A06A',
        roughness: 0.8, metalness: 0, cutPatternId: 'fp-parallel-0', tileSize: 0.3, bumpStrength: 0.15,
        density: 545, youngsModulus: 9650, shearModulus: 730, poissonRatio: 0.35, conductivity: 0.144, specificHeat: 2000,
    },
    {
        id: 'Aluminum', name: 'Aluminum', cls: 'Metal', color: '#D7D9DC',
        roughness: 0.3, metalness: 0.9, cutPatternId: 'fp-parallel-45',
        density: 2706, youngsModulus: 71714, shearModulus: 25515, poissonRatio: 0.33, yieldStrength: 240,
        thermalExpansion: 0.0000221, conductivity: 168.75, specificHeat: 899,
    },
    {
        id: 'Glass', name: 'Glass', cls: 'Glass', color: '#BFE3E8',
        roughness: 0.05, metalness: 0, transparency: 0.75, cutPatternId: 'fp-crosshatch',
        density: 2580, youngsModulus: 50000, poissonRatio: 0.22, conductivity: 1, specificHeat: 800, emissivity: 0.84,
    },
    {
        id: 'Insulation', name: 'Thermal/Moisture - Insulation', cls: 'Finishes', color: '#F5E14E',
        roughness: 1, metalness: 0, cutPatternId: 'fp-insulation', tileSize: 0.6,
        density: 30, conductivity: 0.04, specificHeat: 1210, description: 'Mineral wool batt.',
    },
    {
        id: 'Membrane', name: 'Roofing - Single Ply', cls: 'Membrane', color: '#3A3A3A',
        roughness: 0.7, metalness: 0.1, cutPatternId: 'fp-solid',
        density: 1200, youngsModulus: 1000, conductivity: 0.25, specificHeat: 1500,
    },
    {
        id: 'Paint', name: 'Paint - Interior', cls: 'Finishes', color: '#F5F5F0',
        roughness: 0.6, metalness: 0, cutPatternId: 'fp-none',
        density: 1300, conductivity: 0.2, specificHeat: 1000,
    },
    {
        id: 'Terrazzo', name: 'Flooring - Terrazzo', cls: 'Finishes', color: '#CFC9BE',
        roughness: 0.35, metalness: 0, cutPatternId: 'fp-concrete', surfacePatternId: 'fp-concrete',
        tileSize: 0.6, bumpStrength: 0.1,
        density: 2200, youngsModulus: 20000, conductivity: 1.05, specificHeat: 800,
    },
    {
        id: 'Ceramic', name: 'Flooring - Ceramic Tile 60x60', cls: 'Finishes', color: '#DDD8CF',
        roughness: 0.25, metalness: 0, cutPatternId: 'fp-crosshatch', surfacePatternId: 'fp-crosshatch',
        tileSize: 0.6, bumpStrength: 0.2, description: '600 x 600 mm ceramic tile.',
        density: 2300, youngsModulus: 30000, conductivity: 1.0, specificHeat: 800,
    },
    {
        id: 'Steel - Structural', name: 'Steel - Structural', cls: 'Metal', color: '#8E9299',
        roughness: 0.45, metalness: 0.9, cutPatternId: 'fp-parallel-45',
        density: 7850, youngsModulus: 205000, shearModulus: 79300, poissonRatio: 0.3,
        yieldStrength: 345, thermalExpansion: 0.0000117, conductivity: 44.947, specificHeat: 482.6,
        keynote: 'S001', costBasis: 'kg',
    },
];

/**
 * Build the fresh material + asset collections for a new project.
 * Generated once per document so material ids remain stable within a file,
 * and legacy catalog entries are appended from the takeoff database.
 */
export function createDefaultMaterialLibrary(): MaterialLibrarySeed {
    const materials: BIMMaterial[] = [];
    const assets: AppearanceAsset[] = [];

    for (const spec of SEED_SPECS) {
        const { material, asset } = seed(spec);
        materials.push(material);
        assets.push(asset);
    }

    // Legacy takeoff catalog entries (concrete-c25, rebar-b500, ...) so
    // CATEGORY_MATERIALS and existing element parameters keep resolving.
    const existingIds = new Set(materials.map((m) => m.id));
    for (const catalogMat of STANDARD_MATERIALS) {
        if (existingIds.has(catalogMat.id)) continue;
        const cls: MaterialClass =
            catalogMat.category === 'Concrete' ? 'Concrete'
                : catalogMat.category === 'Steel' ? 'Metal'
                    : catalogMat.category === 'Masonry' ? 'Masonry'
                        : catalogMat.category === 'Finishes' ? 'Finishes'
                            : 'Miscellaneous';
        const { material, asset } = seed({
            id: catalogMat.id,
            name: catalogMat.name,
            cls,
            color: cls === 'Metal' ? '#9FA4AB' : cls === 'Concrete' ? '#B8B8B4' : cls === 'Masonry' ? '#A9A39A' : '#CCCCCC',
            roughness: cls === 'Metal' ? 0.45 : 0.9,
            metalness: cls === 'Metal' ? 0.9 : 0,
            cutPatternId: cls === 'Concrete' ? 'fp-concrete' : 'fp-none',
            density: catalogMat.density,
            cost: String(catalogMat.unitCost),
            costBasis: catalogMat.unit,
            description: catalogMat.name,
        });
        material.identity.wasteFactor = catalogMat.wasteFactor;
        material.identity.takeoffUnit = catalogMat.unit;
        materials.push(material);
        assets.push(asset);
    }

    return { materials, assets };
}

/** Mint a brand-new material (browser "New Material" button). */
export function createBlankMaterial(name: string, classOverride?: MaterialClass): { material: BIMMaterial; asset: AppearanceAsset } {
    const materialId = uuidv4();
    const asset: AppearanceAsset = {
        id: uuidv4(),
        name,
        color: '#CCCCCC',
        roughness: 0.9,
        metalness: 0,
        transparency: 0,
        imageId: '',
        tileSize: 1,
        bumpStrength: 0,
        emissionColor: '',
        emissionStrength: 0,
    };
    const material: BIMMaterial = {
        id: materialId,
        name,
        appearanceAssetId: asset.id,
        identity: defaultIdentity(classOverride),
        graphics: defaultGraphics({ cutPatternId: 'fp-none' }),
        physical: defaultPhysical(),
        thermal: defaultThermal(),
    };
    return { material, asset };
}
